import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    IconButton,
    CircularProgress,
} from '@material-ui/core';
import { artifactListStyles } from '../../../../assets/styles/jss/pages/ia/ArtifactListStyles';
import { listArtifacts, getArtifact } from '../../../../services/artifact-api';
import ArtifactSidebar from '../Chat/ArtifactSidebar';

// Maps an artifact approval status to its list badge (label + style key). This
// is the passive at-a-glance signal: a PRD the manager sent back reads
// "Cambios solicitados", not "Borrador", so the author spots it on entry.
// Unknown/missing status falls back to the draft badge.
function statusBadge(status, classes) {
    switch (status) {
        case 'approved':
            return { label: 'Aprobado', className: classes.badgeApproved };
        case 'pending_approval':
            return { label: 'En revisión', className: classes.badgePending };
        case 'changes_requested':
            return { label: 'Cambios solicitados', className: classes.badgeChangesRequested };
        case 'rejected':
            return { label: 'Rechazado', className: classes.badgeRejected };
        default:
            return { label: 'Borrador', className: classes.badgeDraft };
    }
}

export default function ArtifactList() {
    const classes = artifactListStyles();
    const [artifacts, setArtifacts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedArtifact, setSelectedArtifact] = useState(null);

    const loadArtifacts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await listArtifacts(50);
            setArtifacts(result.artifacts || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadArtifacts();
    }, [loadArtifacts]);

    const handleOpenArtifact = async (artifact) => {
        try {
            const detail = await getArtifact(artifact.artifactId);
            setSelectedArtifact({
                id: detail.artifactId,
                title: detail.title,
                fileName: detail.fileName,
                content: detail.content,
                mimeType: detail.mimeType,
                generatedAt: new Date(detail.createdAt).toISOString(),
                version: detail.version,
                currentVersion: detail.currentVersion,
                status: detail.status,
                decisionComment: detail.decisionComment,
                managerEmail: detail.managerEmail,
            });
        } catch (err) {
            console.error('Failed to open artifact:', err);
        }
    };

    const handleCloseSidebar = () => {
        setSelectedArtifact(null);
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <Box className={classes.root}>
                <Box className={classes.loading}>
                    <CircularProgress size={32} style={{ color: '#8b5cf6' }} />
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box className={classes.root}>
                <Box className={classes.empty}>
                    <Typography variant="body1">Error al cargar artifacts</Typography>
                    <Typography variant="body2" color="textSecondary">{error}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.listContainer}>
                <Box className={classes.header}>
                    <Typography variant="h6" style={{ fontWeight: 700, color: '#1e1b4b' }}>
                        Mis PRDs
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {artifacts.length} documento{artifacts.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>

                {artifacts.length === 0 ? (
                    <Box className={classes.empty}>
                        <i className="ri-draft-line" style={{ fontSize: '2rem', color: '#9ca3af' }} />
                        <Typography variant="body1">No hay artifacts aún</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Genera un PRD desde el chat para verlo aquí.
                        </Typography>
                    </Box>
                ) : (
                    Object.entries(
                        artifacts.reduce((groups, artifact) => {
                            const key = artifact.sessionId || 'sin-conversacion';
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(artifact);
                            return groups;
                        }, {})
                    ).map(([sessionId, groupArtifacts]) => (
                        <Box key={sessionId} style={{ marginBottom: 24 }}>
                            <Typography
                                variant="subtitle2"
                                style={{ color: '#6b7280', marginBottom: 8, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                            >
                                Conversación: {groupArtifacts[0].title.split(' - ')[0] || sessionId.slice(0, 8)}
                            </Typography>
                            <Box className={classes.grid}>
                                {groupArtifacts.map((artifact) => (
                                    <Paper
                                        key={artifact.artifactId}
                                        className={classes.card}
                                        elevation={0}
                                        onClick={() => handleOpenArtifact(artifact)}
                                    >
                                        <Typography className={classes.cardTitle}>
                                            {artifact.title}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                                            {artifact.fileName}
                                        </Typography>
                                        <Box className={classes.cardMeta}>
                                            {(() => {
                                                const badge = statusBadge(artifact.status, classes);
                                                return (
                                                    <Chip
                                                        label={badge.label}
                                                        size="small"
                                                        className={`${classes.badge} ${badge.className}`}
                                                    />
                                                );
                                            })()}
                                            <Typography className={classes.version}>
                                                v{artifact.currentVersion}
                                            </Typography>
                                            <Typography className={classes.date}>
                                                {formatDate(artifact.updatedAt || artifact.createdAt)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            {selectedArtifact && (
                <ArtifactSidebar artifact={selectedArtifact} onClose={handleCloseSidebar} />
            )}
        </Box>
    );
}
